import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import {
	Table,
	Modal,
	ModalFooter,
	Row,
	Col,
	Button,
	Form,
	FormGroup,
	Input,
	Label
} from "reactstrap";
import { ArrowRight, CheckCircle, Box } from "react-feather";
import { RouteComponentProps, withRouter } from "react-router-dom";
import packageImagePlaceholder from "../../assets/images/product-packaging-placeholder.png";
import { IApplicationState } from "../../redux/reducers";
import { routes } from "../routes";
import SuitabilityBar from "../../components/suitabilityBar";
import packagingService, { IPackageFilters } from "../../services/packagingService";
import { IPackage, IPackagePreference } from "../../interfaces/package";
import { IPaginateList } from "../../interfaces/pagination";
import { IUser } from "../../interfaces/user";
import { formatPackagingFilters } from "../../utils/packaging";
import { ImageSkeleton, TableSkeleton } from "../../utils/ghostUI";
import ReactImageFallback from "react-image-fallback";
import Upgrade from "../../components/common/upgrade";
import { paginateAction } from "../../redux/actions/pagination/pagination";
import { selectPackageAction } from "../../redux/actions/packaging/packaging";
import { packaging } from "../../redux/actions/actionContants";
import { Dispatch } from "redux";
import { FaBrain } from "react-icons/fa";
import { GiWeight } from "react-icons/gi";
import { MdErrorOutline } from "react-icons/md";
import AIAgentBox from "../../components/common/AIAgentBox";
import AIAnalysisResultsModal from "../../components/modals/AIAnalysisResultsModal";

interface IProps extends RouteComponentProps {
	packages: IPaginateList<IPackage[]>;
	userId: any;
	profile: IUser | undefined;
	getAllPackages: (userId: string) => Promise<any>;
	paginate: (page: number) => void;
	setSelectedPackage: (p: IPackage) => Promise<void>;
	getPackagingPreferences: () => Promise<any>;
	getFilteredPackages: (filters?: IPackageFilters) => Promise<any>;
}

const AdminPackagingTable: React.FC<IProps> = ({
	packages: { list, pagination },
	userId,
	getAllPackages,
	getFilteredPackages,
	paginate,
	...props
}) => {
	const [modal, setModal] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedPackage, setSelectedPackage] = useState<IPackage>();
	const [viewMore, setViewMore] = useState<boolean>(false);
	const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);
	const [appliedFilters, setAppliedFilters] = useState<any>();
	const [showAllPackages, setShowAllPackages] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");

	// Pagination state
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [itemsPerPage] = useState<number>(10);

	const selectPackage = (p: IPackage) => {
		setViewMore(false);
		setSelectedPackage(p);
		setModal(true);
	};

	// Filter + paginate
	const filteredPackages = useMemo(() => {
		if (!search.trim()) return list;
		return list.filter(
			(pkg: IPackage) =>
				pkg.name.toLowerCase().includes(search.toLowerCase()) ||
				pkg.material?.some((m: string) => m.toLowerCase().includes(search.toLowerCase())) ||
				pkg.region_available?.some((r: string) => r.toLowerCase().includes(search.toLowerCase())) ||
				pkg.status.toLowerCase().includes(search.toLowerCase())
		);
	}, [list, search]);

	const paginatedPackages = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredPackages.slice(startIndex, endIndex);
	}, [filteredPackages, currentPage, itemsPerPage]);

	const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);

	useEffect(() => {
		setCurrentPage(1);
	}, [search]);

	useEffect(() => {
		if (userId) {
			setLoading(true);
			if (appliedFilters || showAllPackages) {
				let formatted: IPackageFilters | undefined = formatPackagingFilters(appliedFilters);
				getFilteredPackages(formatted).finally(() => setLoading(false));
			} else {
				getAllPackages(userId).finally(() => setLoading(false));
			}
		}
	}, [userId, appliedFilters, showAllPackages, pagination.page]);

	const handlePackageNav = (p: IPackage) => {
		props.setSelectedPackage(p).finally(() => {
			props.history.push(`${routes.PACKAGING}/${p.id}`);
		});
	};

	const handlePrevious = () => {
		if (currentPage > 1) setCurrentPage(currentPage - 1);
	};

	const handleNext = () => {
		if (currentPage < totalPages) setCurrentPage(currentPage + 1);
	};

	return (
		<div className="container-fluid">
			<style>
				{`
					.packaging-table {
						min-width: 900px !important;
					}
					
					.packaging-table th:nth-child(1) { min-width: 250px; } /* Package Name */
					.packaging-table th:nth-child(2) { min-width: 100px; } /* Status */
					.packaging-table th:nth-child(3) { min-width: 120px; } /* Market */
					.packaging-table th:nth-child(4) { min-width: 200px; } /* Material */
					.packaging-table th:nth-child(5) { min-width: 150px; } /* Packaging Score */
					.packaging-table th:nth-child(6) { min-width: 100px; } /* Actions */
					
					.packaging-table td:nth-child(1) { min-width: 250px; }
					.packaging-table td:nth-child(2) { min-width: 100px; }
					.packaging-table td:nth-child(3) { min-width: 120px; }
					.packaging-table td:nth-child(4) { min-width: 200px; }
					.packaging-table td:nth-child(5) { min-width: 150px; }
					.packaging-table td:nth-child(6) { min-width: 100px; }
					
					.material-badge {
						display: inline-block;
						background: #f8f9fa;
						color: #495057;
						padding: 4px 8px;
						border-radius: 4px;
						font-size: 11px;
						font-weight: 500;
						border: 1px solid #e9ecef;
						margin: 1px;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						max-width: 100px;
					}
				`}
			</style>
			{/* Header with search + button */}

			{/* AI Agent Card */}
			<AIAgentBox
				title="AI Packaging Agent"
				description="Analyze packaging data completeness across type, material, weight, and sustainability metrics with automated enrichment from BOMs and packaging databases"
				features={[
					{ icon: <CheckCircle />, text: "Packaging type classification", iconColor: "#22bb77" },
					{ icon: <GiWeight />, text: "Material & weight analysis", iconColor: "#3f65f1" },
					{ icon: <FaBrain />, text: "Sustainability metrics", iconColor: "#19be6e" },
					{ icon: <MdErrorOutline />, text: "Flag high-volume gaps", iconColor: "#ff5722" }
				]}
				theme="orange"
				onButtonClick={() => setIsAIAnalysisModalOpen(true)}
			/>

			{/* Search */}
			<div className="admin-data-search">
				<div className="admin-data-search__input-wrapper">
					<input
						type="text"
						placeholder="Search packaging by name, material, region, or status..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="admin-data-search__input"
						aria-label="Search packaging"
					/>
					<i className="fas fa-search admin-data-search__icon"></i>
					{search && (
						<button
							type="button"
							className="admin-data-search__clear-btn"
							onClick={() => setSearch("")}
							aria-label="Clear search"
						>
							<i className="fas fa-times"></i>
						</button>
					)}
				</div>
				<div className="admin-data-search__actions">
					<button
						type="button"
						className="admin-data-search__button btn-primary"
						onClick={() => { }}
					>
						<i className="fas fa-plus"></i>
						Add Packaging
					</button>
				</div>
			</div>

			{/* Table */}
			<div className="admin-data-table-wrapper">
				<div className="table-responsive">
					<Table className="admin-data-table packaging-table">
						<thead>
							<tr className="table-header">
								<th>Package Name</th>
								<th>Status</th>
								<th>Market</th>
								<th>Material</th>
								<th>Packaging Score</th>
								<th className="actions-column">Actions</th>
							</tr>
						</thead>
						<tbody>
							{props.profile?.company?.subscription_type === "supplier" ? (
								<tr>
									<td colSpan={6} className="empty-cell">
										<div className="empty-state">
											<div className="empty-icon">📦</div>
											<span>Upgrade required to view packaging data</span>
										</div>
									</td>
								</tr>
							) : loading ? (
								<tr>
									<td colSpan={6} className="loading-cell">
										<div className="loading-spinner">
											<div className="spinner"></div>
											<span>Loading packaging...</span>
										</div>
									</td>
								</tr>
							) : paginatedPackages.length === 0 ? (
								<tr>
									<td colSpan={6} className="empty-cell">
										<div className="empty-state">
											<div className="empty-icon">📦</div>
											<span>{search ? "No packaging matches your search" : "No packaging found"}</span>
										</div>
									</td>
								</tr>
							) : (
								paginatedPackages.map((p: IPackage) => (
									<tr className="table-row clickable" key={p.id} onClick={() => handlePackageNav(p)}>
										<td className="cell-name" title={p.name}>
											<div style={{ display: 'flex', alignItems: 'center', gap: '12px', maxWidth: '250px' }}>
												<ReactImageFallback
													src={p.image_uri || ""}
													initialImage={<ImageSkeleton className="table-img-ghost" />}
													fallbackImage={packageImagePlaceholder}
													style={{ width: 40, height: 40, objectFit: "cover", borderRadius: '6px', flexShrink: 0 }}
												/>
												<span style={{
													fontWeight: 500,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													flex: 1
												}}>
													{p.name}
												</span>
											</div>
										</td>
										<td className="cell-status">
											<span className={`status-badge status-${(p.status || "active").toLowerCase()}`}>
												{p.status || "Active"}
											</span>
										</td>
										<td title={p?.region_available?.join(", ")}>
											{p?.region_available && p.region_available.length > 0 ? (
												<span style={{
													display: 'inline-block',
													maxWidth: '120px',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap'
												}}>
													{p.region_available.join(", ")}
												</span>
											) : (
												<span style={{ color: '#6c757d' }}>N/A</span>
											)}
										</td>
										<td className="cell-material">
											{p?.material && p.material.length > 0 ? (
												<div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '200px' }}>
													{p.material.slice(0, 2).map((material, index) => (
														<span key={index} className="material-badge" title={material}>
															{material.length > 15 ? `${material.substring(0, 15)}...` : material}
														</span>
													))}
													{p.material.length > 2 && (
														<span className="material-badge" title={`+${p.material.length - 2} more`}>
															+{p.material.length - 2}
														</span>
													)}
												</div>
											) : (
												<span style={{ color: '#6c757d' }}>N/A</span>
											)}
										</td>
										<td>
											<SuitabilityBar
												title="Score"
												type="packaging"
												className="persona-suitability"
												value={p.packaging_score || 0}
											/>
										</td>
										<td className="actions-cell">
											<div className="btn-group">
												<button
													type="button"
													className="action-btn btn-view"
													onClick={(e) => {
														e.stopPropagation();
														selectPackage(p);
													}}
												>
													<ArrowRight size={14} className="mr-1" /> View
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</div>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="admin-data-pagination">
					<div className="admin-data-pagination__controls">
						<button
							type="button"
							className="admin-data-pagination__button btn-prev"
							disabled={currentPage === 1}
							onClick={handlePrevious}
						>
							<i className="fas fa-chevron-left"></i>
							Previous
						</button>
					</div>
					<div className="admin-data-pagination__info">
						<div className="page-info">
							Page {currentPage} of {totalPages}
						</div>
						{search && (
							<div className="results-info">
								{filteredPackages.length} results
							</div>
						)}
					</div>
					<div className="admin-data-pagination__next-controls">
						<button
							type="button"
							className="admin-data-pagination__button btn-next"
							disabled={currentPage === totalPages}
							onClick={handleNext}
						>
							Next
							<i className="fas fa-chevron-right"></i>
						</button>
					</div>
				</div>
			)}

			{/* Supplier Upgrade */}
			{props.profile?.company.subscription_type === "supplier" && (
				<div className="mt-5 w-100">
					<Upgrade />
				</div>
			)}


			{/* Modal */}
			<Modal isOpen={modal} className="package-modal" style={{ maxWidth: "95%", margin: "0 auto" }}>
				<Row className="package-header p-2">
					<span className="cost">$ $ $</span>
					<span className={`label label-${selectedPackage?.status?.toLowerCase()}`}>
						{selectedPackage?.status?.toLowerCase()}
					</span>
				</Row>

				<Row>
					<div className="img-container p-2">
						<img src={packageImagePlaceholder} style={{ maxWidth: "100%", height: "auto" }} />
					</div>
					<Col>
						<h3>{selectedPackage?.name}</h3>
						<Row className="info">
							<Col xs={12} md={4}>
								<span>Market</span>
								<p>{selectedPackage?.region_available?.join(", ")}</p>
							</Col>
							<Col xs={12} md={8}>
								<span>Material</span>
								<p>{selectedPackage?.material?.join(", ")}</p>
							</Col>
						</Row>
					</Col>
				</Row>

				<Row className="tags m-2">
					<Col>
						{selectedPackage?.sustainability?.map((s: IPackagePreference, index: number) => (
							<span key={index} className="tag mr-2">
								<CheckCircle size={16} className="mr-1" /> {s.title}
							</span>
						))}
						{selectedPackage?.filling_process?.map((f: IPackagePreference, index: number) => (
							<span key={index} className="tag mr-2">
								<CheckCircle size={16} className="mr-1" /> {f.title}
							</span>
						))}
					</Col>
				</Row>

				<Row className="m-2">
					<Col xs={12}>
						<SuitabilityBar
							title="Packaging score"
							type="packaging"
							className="persona-suitability"
							value={selectedPackage?.packaging_score || 0}
						/>
					</Col>
				</Row>

				<ModalFooter>
					<div
						className="modal-close text-primary"
						style={{ cursor: "pointer" }}
						onClick={() => setModal(false)}
					>
						Close
					</div>
				</ModalFooter>
			</Modal>

			{/* AI Analysis Results Modal */}
			<AIAnalysisResultsModal
				isOpen={isAIAnalysisModalOpen}
				toggle={() => setIsAIAnalysisModalOpen(false)}
				collectionName="packaging"
				collectionLabel="Packaging"
				onAnalysisComplete={() => {
					// Refresh packaging list after analysis is applied
					if (userId) {
						getAllPackages(userId);
					}
				}}
			/>
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	packages: state.packaging.packaging,
	userId: state.user.user_id,
	profile: state.user.profile
});

const mapDispatchToProps = {
	getAllPackages: (userId: string) => packagingService.getAllPackages(userId),
	getFilteredPackages: (filters?: IPackageFilters) => packagingService.getFilteredPackages(filters),
	paginate: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAction(packaging.UPDATE_PAGINATION, page));
	},
	setSelectedPackage: (p: IPackage) => async (dispatch: Dispatch) => {
		dispatch(selectPackageAction(p));
	},
	getPackagingPreferences: () => Promise.resolve([])
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AdminPackagingTable));
